import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  ArrowLeft, User, Phone, MapPin, Package,
  Navigation, PhoneCall, Clock, CheckCircle, AlertCircle,
  ShoppingBag, Truck
} from 'lucide-react'
import { api } from '../../lib/api'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface OrderItem {
  id: string
  quantity: number
  unit_price: number
  product: {
    id: string
    name: string
    image_url?: string
  }
  variant?: {
    variant_name: string
  }
}

interface Order {
  id: string
  store_id: string
  customer_name: string
  customer_phone: string
  customer_email?: string
  delivery_address?: string
  status: 'pending' | 'preparing' | 'ready' | 'ready_for_pickup' | 'picked_up' | 'delivering' | 'delivered' | 'cancelled'
  total_amount: number
  delivery_fee: number
  estimated_delivery_time?: number
  assigned_delivery_id?: string
  created_at: string
  items: OrderItem[]
  notes?: string
}

const DeliveryOrderDetails = () => {
  const navigate = useNavigate()
  const { orderId } = useParams<{ orderId: string }>()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState(false)

  useEffect(() => {
    if (orderId) {
      loadOrder()
    }
  }, [orderId])

  const loadOrder = async () => {
    if (!orderId) return
    
    const cacheKey = `delivery_order_details_${orderId}`
    
    // Intentar cargar desde cache primero
    const cachedOrder = getCacheStale<Order>(cacheKey)
    if (cachedOrder) {
      setOrder(cachedOrder)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/orders/${orderId}`)
      if (response.data.success) {
        const orderData = response.data.data
        const normalizedOrder: Order = {
          id: orderData.id,
          store_id: orderData.store_id || orderData.storeId,
          customer_name: orderData.customer_name || orderData.customerName,
          customer_phone: orderData.customer_phone || orderData.customerPhone,
          customer_email: orderData.customer_email || orderData.customerEmail,
          delivery_address: orderData.delivery_address || orderData.deliveryAddress || orderData.address?.street,
          status: orderData.status,
          total_amount: Number(orderData.total_amount || orderData.totalAmount) || 0,
          delivery_fee: Number(orderData.delivery_fee || orderData.deliveryFee) || 0,
          estimated_delivery_time: Number(orderData.estimated_delivery_time || orderData.estimatedDeliveryTime) || 0,
          assigned_delivery_id: orderData.assigned_delivery_id || orderData.assignedDeliveryId,
          created_at: orderData.created_at || orderData.createdAt,
          items: (orderData.items || []).map((item: any) => ({
            id: item.id,
            quantity: Number(item.quantity) || 0,
            unit_price: Number(item.unit_price || item.unitPrice) || 0,
            product: {
              id: item.product?.id || item.product_id,
              name: item.product?.name || 'Producto',
              image_url: item.product?.image_url || item.product?.imageUrl
            },
            variant: item.variant ? {
              variant_name: item.variant.variant_name || item.variant.variantName
            } : undefined
          })),
          notes: orderData.notes
        }
        setOrder(normalizedOrder)
        
        // Guardar en cache (30 segundos de TTL - muy dinámico)
        setCache(cacheKey, normalizedOrder, 30 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading order:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrder) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar el pedido')
      navigate(-1)
    } finally {
      setLoading(false)
    }
  }

  const handlePickup = async () => {
    if (!order) return
    try {
      setUpdatingStatus(true)
      const response = await api.pickupOrder(order.id)
      if (response.success) {
        toast.success('Pedido marcado como recogido')
        navigate(-1)
      } else {
        toast.error(response.message || 'Error al marcar pedido como recogido')
      }
    } catch (error: any) {
      console.error('Error picking up order:', error)
      toast.error(error.response?.data?.message || 'Error al marcar pedido como recogido')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const handleStartDelivery = async () => {
    if (!order) return
    try {
      setUpdatingStatus(true)
      const response = await api.startDelivery(order.id)
      if (response.success) {
        toast.success('Entrega iniciada')
        navigate(-1)
      } else {
        toast.error(response.message || 'Error al iniciar entrega')
      }
    } catch (error: any) {
      console.error('Error starting delivery:', error)
      toast.error(error.response?.data?.message || 'Error al iniciar entrega')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const handleCompleteDelivery = () => {
    if (!order) return
    navigate(`/store/delivery/orders/${order.id}/complete`)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready_for_pickup': return 'bg-amber-500'
      case 'picked_up': return 'bg-blue-500'
      case 'delivering': return 'bg-purple-500'
      case 'delivered': return 'bg-green-500'
      default: return 'bg-gray-500'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ready_for_pickup': return 'Lista para Recoger'
      case 'picked_up': return 'Recogida'
      case 'delivering': return 'En Camino'
      case 'delivered': return 'Entregado'
      default: return status
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <div className="flex items-center justify-center h-screen">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100 border-t-blue-600"></div>
            <div className="absolute inset-0 animate-spin rounded-full h-16 w-16 border-4 border-transparent border-r-indigo-600" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          </div>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-gray-400" />
            </div>
            <p className="text-lg font-bold text-gray-900">Pedido no encontrado</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 pb-24">
      {/* Header - Modern Design */}
      <div className="bg-white/70 backdrop-blur-2xl border-b border-white/60 sticky top-0 z-10">
        <div className="max-w-md lg:max-w-7xl mx-auto px-4 lg:px-8 py-5 lg:py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-3 lg:p-4 -ml-2 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all active:scale-95 shadow-lg shadow-black/5"
            >
              <ArrowLeft className="w-6 h-6 lg:w-7 lg:h-7 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Detalles del Pedido
              </h1>
              <p className="text-xs lg:text-sm text-gray-500 font-medium mt-0.5">#{order.id.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-md lg:max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-8 space-y-5 lg:space-y-0">
          {/* Left Column */}
          <div className="space-y-5 lg:space-y-6">
            {/* Status Badge - Modern Design */}
            <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-bold text-gray-500 uppercase tracking-wider mb-2 lg:mb-3">Estado</p>
                  <p className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    {getStatusLabel(order.status)}
                  </p>
                </div>
                <div className={`w-20 h-20 lg:w-24 lg:h-24 rounded-3xl ${getStatusColor(order.status)} flex items-center justify-center shadow-xl`}>
                  {order.status === 'ready_for_pickup' && <Package className="w-10 h-10 lg:w-12 lg:h-12 text-white" />}
                  {order.status === 'picked_up' && <ShoppingBag className="w-10 h-10 lg:w-12 lg:h-12 text-white" />}
                  {order.status === 'delivering' && <Truck className="w-10 h-10 lg:w-12 lg:h-12 text-white" />}
                  {order.status === 'delivered' && <CheckCircle className="w-10 h-10 lg:w-12 lg:h-12 text-white" />}
                </div>
              </div>
            </div>

            {/* Customer Info - Modern Design */}
            <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
              <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-5 lg:mb-6 flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <User className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                Información del Cliente
              </h2>
              <div className="space-y-4 lg:space-y-5">
                <div className="flex items-center gap-4 lg:gap-5">
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <User className="w-6 h-6 lg:w-7 lg:h-7 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs lg:text-sm font-bold text-gray-500 uppercase tracking-wider mb-1 lg:mb-2">Nombre</p>
                    <p className="font-bold text-gray-900 text-base lg:text-lg">{order.customer_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 lg:gap-5">
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center">
                    <Phone className="w-6 h-6 lg:w-7 lg:h-7 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs lg:text-sm font-bold text-gray-500 uppercase tracking-wider mb-1 lg:mb-2">Teléfono</p>
                    <a href={`tel:${order.customer_phone}`} className="font-bold text-blue-600 text-base lg:text-lg">
                      {order.customer_phone}
                    </a>
                  </div>
                  <a
                    href={`tel:${order.customer_phone}`}
                    className="px-5 lg:px-6 py-3 lg:py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-2xl lg:rounded-3xl font-bold text-sm lg:text-base shadow-xl shadow-emerald-500/40 active:scale-95 transition-transform"
                  >
                    <PhoneCall className="w-5 h-5 lg:w-6 lg:h-6" />
                  </a>
                </div>
                {order.delivery_address && (
                  <div className="flex items-start gap-4 lg:gap-5">
                    <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
                      <MapPin className="w-6 h-6 lg:w-7 lg:h-7 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs lg:text-sm font-bold text-gray-500 uppercase tracking-wider mb-1 lg:mb-2">Dirección de Entrega</p>
                      <p className="font-bold text-gray-900 text-base lg:text-lg">{order.delivery_address}</p>
                    </div>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.delivery_address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-5 lg:px-6 py-3 lg:py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-2xl lg:rounded-3xl font-bold text-sm lg:text-base shadow-xl shadow-blue-500/40 active:scale-95 transition-transform"
                    >
                      <Navigation className="w-5 h-5 lg:w-6 lg:h-6" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-5 lg:space-y-6">
            {/* Products - Modern Design */}
            <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
              <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-5 lg:mb-6 flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <Package className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                Productos ({order.items.length})
              </h2>
              <div className="space-y-3 lg:space-y-4">
                {order.items.map((item, index) => (
                  <div key={item.id} className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl lg:rounded-3xl p-5 lg:p-6 border border-gray-200/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 lg:mb-3">
                          <span className="text-xs lg:text-sm font-bold text-gray-400 bg-white px-2 py-1 lg:px-3 lg:py-1.5 rounded-lg">#{index + 1}</span>
                          <span className="font-bold text-gray-900 text-base lg:text-lg">{item.product.name}</span>
                        </div>
                        {item.variant && (
                          <p className="text-sm lg:text-base text-gray-600 ml-8 lg:ml-10 font-medium">- {item.variant.variant_name}</p>
                        )}
                        <div className="mt-3 lg:mt-4 ml-8 lg:ml-10 flex items-center gap-5 lg:gap-6 text-sm lg:text-base">
                          <span className="text-gray-600 font-medium">
                            Cantidad: <span className="font-bold text-gray-900">{item.quantity}</span>
                          </span>
                          <span className="text-gray-600 font-medium">
                            Precio: <span className="font-bold text-gray-900">RD${item.unit_price.toFixed(2)}</span>
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                          RD${(item.unit_price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary - Modern Design */}
            <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
              <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-5 lg:mb-6">Resumen del Pedido</h2>
              <div className="space-y-4 lg:space-y-5">
                <div className="flex justify-between text-sm lg:text-base">
                  <span className="text-gray-600 font-medium">Subtotal:</span>
                  <span className="font-bold text-gray-900">RD${order.total_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm lg:text-base">
                  <span className="text-gray-600 font-medium">Costo de Entrega:</span>
                  <span className="font-bold text-gray-900">RD${order.delivery_fee.toFixed(2)}</span>
                </div>
                {order.estimated_delivery_time && (
                  <div className="flex items-center gap-2 text-sm lg:text-base text-gray-600 pt-3 lg:pt-4 border-t border-gray-200">
                    <Clock className="w-4 h-4 lg:w-5 lg:h-5" />
                    <span className="font-medium">Tiempo estimado: {order.estimated_delivery_time} minutos</span>
                  </div>
                )}
                <div className="flex justify-between text-xl lg:text-2xl font-bold pt-4 lg:pt-5 border-t-2 border-gray-300">
                  <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Total:</span>
                  <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    RD${(order.total_amount + order.delivery_fee).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes - Modern Design */}
            {order.notes && (
              <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
                <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-4 lg:mb-5">Notas Especiales</h2>
                <p className="text-gray-700 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl lg:rounded-3xl p-5 lg:p-6 font-medium border border-gray-200/50 text-sm lg:text-base">
                  {order.notes}
                </p>
              </div>
            )}

            {/* Actions - Modern Design */}
            <div className="space-y-3 lg:space-y-4 pb-6">
              {(order.status === 'ready_for_pickup' || order.status === 'ready') && (
                <button
                  onClick={handlePickup}
                  disabled={updatingStatus}
                  className="w-full px-6 lg:px-8 py-5 lg:py-6 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-[2rem] lg:rounded-3xl font-bold text-lg lg:text-xl shadow-2xl shadow-amber-500/40 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-transform"
                >
                  {updatingStatus ? 'Marcando...' : 'Marcar como Recogida'}
                </button>
              )}
              {order.status === 'picked_up' && (
                <button
                  onClick={handleStartDelivery}
                  disabled={updatingStatus}
                  className="w-full px-6 lg:px-8 py-5 lg:py-6 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-[2rem] lg:rounded-3xl font-bold text-lg lg:text-xl shadow-2xl shadow-purple-500/40 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-transform"
                >
                  {updatingStatus ? 'Iniciando...' : 'Iniciar Entrega'}
                </button>
              )}
              {order.status === 'delivering' && (
                <button
                  onClick={handleCompleteDelivery}
                  className="w-full px-6 lg:px-8 py-5 lg:py-6 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-[2rem] lg:rounded-3xl font-bold text-lg lg:text-xl shadow-2xl shadow-emerald-500/40 active:scale-95 transition-transform"
                >
                  Completar Entrega
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeliveryOrderDetails

